import React, { useRef, useState } from "react";

import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  useTheme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
  Logout as LogoutIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { confirmAttendanceForPayroll } from "store/features/company/attendanceManagement/attendanceRecords/attendanceRecordsActions";

const AttendanceMonthlyToggle = ({
  employee,
  year,
  month,
  monthStr,
  daysInMonth,
  theme,
  pagination,
  selectedMonth,
}) => {
  const attendance = employee.attendance || {};
  const themeMode = theme.palette.mode;
  const scrollRef = useRef(null);
  const dispatch = useDispatch();

  // State for confirm dialog
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [error, setError] = useState("");

  // Status color mapping
  const statusColors = {
    P: { bg: "#d1fae5", text: "#065f46", label: "P" },
    SP: { bg: "#d1fae5", text: "#065f46", label: "SP" },
    HP: { bg: "#d1fae5", text: "#065f46", label: "HP" },
    WoP: { bg: "#d1fae5", text: "#065f46", label: "WoP" },
    WOP: { bg: "#d1fae5", text: "#065f46", label: "WoP" },
    LP: { bg: "#d1fae5", text: "#065f46", label: "LP" },
    L: { bg: "#fef3c7", text: "#92400e", label: "L" },
    HPL: { bg: "#fef3c7", text: "#92400e", label: "HPL" },
    A: { bg: "#fee2e2", text: "#991b1b", label: "A" },
    H: { bg: "#dbeafe", text: "#1e40af", label: "H" },
    WO: { bg: "#e0e7ff", text: "#3730a3", label: "WO" },
    SuP: { bg: "#dbeafe", text: "#1e40af", label: "SuP" },
    Sat: { bg: "#dbeafe", text: "#1e40af", label: "Sat" },
    Sun: { bg: "#dbeafe", text: "#1e40af", label: "Sun" },
    "-": { bg: "#f1f5f9", text: "#64748b", label: "-" },
  };
  const defaultStatusColor = { bg: "#cffafe", text: "#0891b2", label: null };

  // Calculate summary statistics for the employee
  const calculateSummary = () => {
    let present = 0,
      absent = 0,
      leave = 0,
      late = 0,
      ontime = 0;
    daysInMonth.forEach((day) => {
      const record = attendance[day] || { status: "A" };
      const status = record.status || "A";

      // Present = P, SP, HP, WoP, WOP, LP, L, HPL
      if (
        status === "P" ||
        status === "SP" ||
        status === "HP" ||
        status === "WoP" ||
        status === "WOP" ||
        status === "LP" ||
        status === "L" ||
        status === "HPL"
      ) {
        present++;
      }
      // Just for late
      if (status === "L" || status === "HPL") late++;
      // Absent = A
      else if (status === "A") {
        absent++;
      }
      // Leave = anything that is NOT in [L, A, P, H, WO, HP, WoP, WOP, LP, SP, HPL, Sat, Sun, SuP]
      else if (
        ![
          "L",
          "A",
          "P",
          "H",
          "WO",
          "HP",
          "WoP",
          "WOP",
          "LP",
          "SP",
          "HPL",
          "Sat",
          "Sun",
          "SuP",
          "-",
        ].includes(status)
      ) {
        leave++;
      }
      ontime = present - late;
    });
    return { present, absent, leave, late, ontime };
  };

  const summary = calculateSummary();
  // State for edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({
    present: summary.present,
    absent: summary.absent,
    leave: summary.leave,
    remarks: "",
  });
  // Handle confirm attendance
  const handleConfirmAttendance = async () => {
    if (!remarks.trim()) {
      setError("Please enter remarks");
      return;
    }

    setConfirming(true);
    setError("");

    let present = 0,
      absent = 0,
      leave = 0;
    daysInMonth.forEach((day) => {
      const record = attendance[day] || { status: "A" };
      const status = record.status || "A";

      if (status === "P" || status === "L") {
        present++;
      } else if (status === "A") {
        absent++;
      } else if (!["L", "A", "P", "H", "WO", "-"].includes(status)) {
        leave++;
      }
    });

    const data = {
      month: selectedMonth.month() + 1,
      year: selectedMonth.year(),
      employee_id: Number(employee.employeeId),
      status: "confirmed",
      total_present_days: Number(present),
      total_absent_days: Number(absent),
      total_leave_days: Number(leave),
      remarks: remarks.trim(),
    };

    console.log("Sending data:", data);

    const params = {
      year: year,
      month: month,
    };

    const result = await dispatch(confirmAttendanceForPayroll(data, params));

    setConfirming(false);

    if (result.success) {
      setConfirmDialogOpen(false);
      setRemarks("");
      setError("");
    }
  };

  return (
    <Paper
      key={employee.employeeId}
      elevation={0}
      sx={{
        borderRadius: "12px",
        border: `2px solid ${themeMode === "dark" ? "#1e293b" : "#e5e7eb"}`,
        backgroundColor: themeMode === "dark" ? "#0f172a" : "#ffffff",
        overflow: "visible",
        position: "relative",
      }}
    >
      <Box
        sx={{
          overflow: "visible",
          position: "relative",
          pb: "12px",
        }}
      >
        <Box
          ref={scrollRef}
          sx={{
            overflowX: "auto",
            overflowY: "hidden",
            pb: 2,
            // Orange scrollbar styles
            "&::-webkit-scrollbar": {
              height: "10px",
              WebkitAppearance: "none",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: themeMode === "dark" ? "#1e293b" : "#f1f5f9",
              borderRadius: "5px",
              margin: "0 12px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#f59e0b",
              borderRadius: "5px",
              "&:hover": {
                backgroundColor: "#d97706",
              },
            },
            // Firefox
            scrollbarWidth: "thin",
            scrollbarColor: "#f59e0b #f1f5f9",
          }}
        >
          <Box sx={{ minWidth: "1500px", p: 3 }}>
            {/* Employee Header  */}
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "#f59e0b",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  flexShrink: 0,
                }}
              >
                {employee.employeeName?.charAt(0) || "E"}
              </Box>

              <Box
                sx={{ display: "flex", alignItems: "center", gap: 3, flex: 1 }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                    {employee.employeeName}
                  </Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                    {employee.employeeCode} • ID: {employee.employeeId}
                  </Typography>
                </Box>

                {/* Confirm Button - beside name */}
                {employee.confirmStatus === "confirm" ? (
                  <Chip
                    label="Confirmed"
                    sx={{
                      bgcolor: "#e5e7eb",
                      color: "#6b7280",
                      fontWeight: 600,
                      height: 28,
                      fontSize: "0.75rem",
                      "& .MuiChip-label": { px: 1.5 },
                    }}
                  />
                ) : (
                  <>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                      onClick={() => setConfirmDialogOpen(true)}
                      sx={{
                        background:
                          "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                        color: "#ffffff",
                        borderRadius: "6px",
                        px: 1.5,
                        py: 0.5,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        textTransform: "none",
                        minHeight: 28,
                        boxShadow: "none",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #059669 0%, #047857 100%)",
                          boxShadow: "0 2px 6px rgba(16, 185, 129, 0.3)",
                        },
                      }}
                    >
                      Confirm
                    </Button>

                    {/* Edit Button - Improved styling */}
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                      onClick={() => {
                        setEditData({
                          present: summary.present,
                          absent: summary.absent,
                          leave: summary.leave,
                          remarks: "",
                        });
                        setEditDialogOpen(true);
                      }}
                      sx={{
                        background:
                          "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                        color: "#ffffff",
                        borderRadius: "6px",
                        px: 1.5,
                        py: 0.5,
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        textTransform: "none",
                        minHeight: 28,
                        boxShadow: "none",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                          boxShadow: "0 2px 6px rgba(245, 158, 11, 0.3)",
                          transform: "translateY(-1px)",
                        },
                        "&:active": {
                          transform: "translateY(0)",
                        },
                      }}
                    >
                      Edit
                    </Button>
                  </>
                )}
              </Box>

              {/* Summary Chips  */}
              <Stack direction="row" spacing={0.75}>
                <Chip
                  label={`Present on-time: ${summary.ontime}`}
                  size="small"
                  sx={{
                    bgcolor: "transparent",
                    color: "#db2777",
                    fontWeight: 600,
                    height: 24,
                    fontSize: "0.7rem",
                    "& .MuiChip-label": {
                      px: 1,
                      py: 0,
                      fontStyle: "italic",
                    },
                  }}
                />
                <Chip
                  label={`Late Present: ${summary.late}`}
                  size="small"
                  sx={{
                    bgcolor: "transparent",
                    color: "#d97706",
                    fontWeight: 600,
                    height: 24,
                    fontSize: "0.7rem",
                    "& .MuiChip-label": {
                      px: 1,
                      py: 0,
                      fontStyle: "italic",
                    },
                  }}
                />
                <Chip
                  label={`Total Present: ${summary.present}`}
                  size="small"
                  sx={{
                    bgcolor: "#d1fae5",
                    color: "#065f46",
                    fontWeight: 600,
                    height: 24,
                    fontSize: "0.7rem",
                    "& .MuiChip-label": { px: 1, py: 0 },
                  }}
                />
                <Chip
                  label={`Total Leave: ${summary.leave}`}
                  size="small"
                  sx={{
                    bgcolor: "#d1d2fa",
                    color: "#04318b",
                    fontWeight: 600,
                    height: 24,
                    fontSize: "0.7rem",
                    "& .MuiChip-label": { px: 1, py: 0 },
                  }}
                />
                <Chip
                  label={`Total Absent: ${summary.absent}`}
                  size="small"
                  sx={{
                    bgcolor: "#fee2e2",
                    color: "#991b1b",
                    fontWeight: 600,
                    height: 24,
                    fontSize: "0.7rem",
                    "& .MuiChip-label": { px: 1, py: 0 },
                  }}
                />
              </Stack>
            </Stack>

            {/* DAY ROW */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(32, minmax(45px, 1fr))`,
                gap: "2px",
                mb: 1.5,
              }}
            >
              <Box
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: "#94a3b8",
                  p: 0.5,
                  textAlign: "center",
                }}
              >
                Day
              </Box>
              {daysInMonth.map((day) => {
                const dateStr = `${year}-${monthStr}-${String(day).padStart(
                  2,
                  "0",
                )}`;
                const date = dayjs(dateStr);
                const isWeekend =
                  date.isValid() && (date.day() === 0 || date.day() === 6);
                return (
                  <Box
                    key={`header-${day}`}
                    sx={{ textAlign: "center", p: 0.5 }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: isWeekend ? "#ef4444" : "#64748b",
                        textAlign: "center",
                      }}
                    >
                      {day}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.6rem",
                        color: isWeekend ? "#ef4444" : "#94a3b8",
                        textAlign: "center",
                      }}
                    >
                      {date.isValid() ? date.format("ddd") : ""}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            {/* CLOCK IN ROW */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(32, minmax(45px, 1fr))`,
                gap: "2px",
                mb: 0.75,
              }}
            >
              <Box
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: "#94a3b8",
                  p: 0.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AccessTimeIcon
                  sx={{ fontSize: 14, color: "#10b981", mr: 0.5 }}
                />
                In
              </Box>
              {daysInMonth.map((day) => {
                const record = attendance[day] || { status: "A", intime: null };
                const inTime = record.intime;
                const status = record.status || "A";
                const isPresent =
                  status === "P" ||
                  status === "SP" ||
                  status === "HP" ||
                  status === "WoP" ||
                  status === "WOP" ||
                  status === "LP";
                const isLate =
                  status === "LP" || status === "L" || status === "HPL";
                const isHoliday =
                  status === "H" ||
                  status === "WO" ||
                  status === "SuP" ||
                  status === "Sat" ||
                  status === "Sun";

                let displayText = "-";
                let textColor = "#94a3b8";
                if (inTime) {
                  displayText = inTime || "-";
                  textColor = isLate || status === "A" ? "#f59e0b" : "#22c55e";
                } else if (isHoliday) {
                  displayText = "H";
                  textColor = "#3b82f6";
                }

                const dateStr = `${year}-${monthStr}-${String(day).padStart(
                  2,
                  "0",
                )}`;
                const date = dayjs(dateStr);
                const isWeekend = date.day() === 0 || date.day() === 6;

                return (
                  <Box
                    key={`in-${employee.employeeId}-${day}`}
                    sx={{
                      p: 0.5,
                      textAlign: "center",
                      backgroundColor:
                        isWeekend && !isHoliday
                          ? themeMode === "dark"
                            ? "rgba(30,41,59,0.3)"
                            : "#fafafa"
                          : "transparent",
                      borderRadius: "4px",
                      border: `1px solid ${
                        themeMode === "dark" ? "#1e293b" : "#f1f5f9"
                      }`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: textColor,
                        textAlign: "center",
                      }}
                    >
                      {displayText}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            {/* CLOCK OUT ROW */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(32, minmax(45px, 1fr))`,
                gap: "2px",
                mb: 0.75,
              }}
            >
              <Box
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: "#94a3b8",
                  p: 0.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LogoutIcon sx={{ fontSize: 14, color: "#ef4444", mr: 0.5 }} />
                Out
              </Box>
              {daysInMonth.map((day) => {
                const record = attendance[day] || {
                  status: "A",
                  outtime: null,
                };
                const outTime = record.outtime;
                const status = record.status || "A";
                const isPresent =
                  status === "P" ||
                  status === "SP" ||
                  status === "HP" ||
                  status === "WoP" ||
                  status === "WOP" ||
                  status === "LP";
                const isLate =
                  status === "LP" || status === "L" || status === "HPL";
                const isHoliday =
                  status === "H" ||
                  status === "WO" ||
                  status === "SuP" ||
                  status === "Sat" ||
                  status === "Sun";

                let displayText = "-";
                let textColor = "#94a3b8";
                if (outTime) {
                  displayText = outTime || "-";
                  textColor = isLate || status === "A" ? "#f59e0b" : "#22c55e";
                } else if (isHoliday) {
                  displayText = "H";
                  textColor = "#3b82f6";
                }

                const dateStr = `${year}-${monthStr}-${String(day).padStart(
                  2,
                  "0",
                )}`;
                const date = dayjs(dateStr);
                const isWeekend = date.day() === 0 || date.day() === 6;

                return (
                  <Box
                    key={`out-${employee.employeeId}-${day}`}
                    sx={{
                      p: 0.5,
                      textAlign: "center",
                      backgroundColor:
                        isWeekend && !isHoliday
                          ? themeMode === "dark"
                            ? "rgba(30,41,59,0.3)"
                            : "#fafafa"
                          : "transparent",
                      borderRadius: "4px",
                      border: `1px solid ${
                        themeMode === "dark" ? "#1e293b" : "#f1f5f9"
                      }`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: textColor,
                        textAlign: "center",
                      }}
                    >
                      {displayText}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            {/* TOTAL HOURS ROW */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(32, minmax(45px, 1fr))`,
                gap: "2px",
                mb: 0.75,
              }}
            >
              <Box
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: "#94a3b8",
                  p: 0.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AccessTimeIcon
                  sx={{ fontSize: 14, color: "#f59e0b", mr: 0.5 }}
                />
                Total
              </Box>
              {daysInMonth.map((day) => {
                const record = attendance[day] || {
                  status: "A",
                  intime: null,
                  outtime: null,
                };
                const inTime = record.intime;
                const outTime = record.outtime;
                const status = record.status || "A";
                const isPresent =
                  status === "P" ||
                  status === "SP" ||
                  status === "HP" ||
                  status === "WoP" ||
                  status === "WOP" ||
                  status === "LP";
                const isLate =
                  status === "LP" || status === "L" || status === "HPL";
                const isHoliday =
                  status === "H" ||
                  status === "WO" ||
                  status === "SuP" ||
                  status === "Sat" ||
                  status === "Sun";

                let displayText = "-";
                let textColor = "#94a3b8";

                if (inTime && outTime) {
                  try {
                    const parseTime = (timeStr) => {
                      const parts = timeStr.trim().split(" ");
                      if (parts.length === 2) {
                        const [time, modifier] = parts;
                        let [hours, minutes] = time.split(":").map(Number);
                        if (modifier === "PM" && hours !== 12) hours += 12;
                        if (modifier === "AM" && hours === 12) hours = 0;
                        return hours * 60 + minutes;
                      }
                      return 0;
                    };
                    const inMins = parseTime(inTime);
                    const outMins = parseTime(outTime);
                    if (inMins > 0 && outMins > 0) {
                      let diff = outMins - inMins;
                      if (diff < 0) diff += 1440;
                      const hrs = Math.floor(diff / 60);
                      const mins = diff % 60;
                      displayText = `${hrs}h ${mins}m`;
                      if (isLate || status === "A") {
                        textColor = "#f59e0b";
                      } else {
                        textColor = "#22c55e";
                      }
                    }
                  } catch (e) {
                    displayText = "-";
                  }
                } else if (isHoliday) {
                  displayText = "H";
                  textColor = "#3b82f6";
                }

                const dateStr = `${year}-${monthStr}-${String(day).padStart(
                  2,
                  "0",
                )}`;
                const date = dayjs(dateStr);
                const isWeekend = date.day() === 0 || date.day() === 6;

                return (
                  <Box
                    key={`total-${employee.employeeId}-${day}`}
                    sx={{
                      p: 0.5,
                      textAlign: "center",
                      backgroundColor:
                        isWeekend && !isHoliday
                          ? themeMode === "dark"
                            ? "rgba(30,41,59,0.3)"
                            : "#fafafa"
                          : "transparent",
                      borderRadius: "4px",
                      border: `1px solid ${
                        themeMode === "dark" ? "#1e293b" : "#f1f5f9"
                      }`,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        color: textColor,
                        textAlign: "center",
                      }}
                    >
                      {displayText}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            {/* STATUS ROW */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: `repeat(32, minmax(45px, 1fr))`,
                gap: "2px",
              }}
            >
              <Box
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: "#94a3b8",
                  p: 0.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                Status
              </Box>
              {daysInMonth.map((day) => {
                const record = attendance[day] || { status: "A" };
                const status = record.status || "A";

                const colorInfo = statusColors[status] || defaultStatusColor;
                const displayText = colorInfo.label || status || "-";
                const bgColor = colorInfo.bg;
                const textColor = colorInfo.text;

                const dateStr = `${year}-${monthStr}-${String(day).padStart(
                  2,
                  "0",
                )}`;
                const date = dayjs(dateStr);
                const isWeekend = date.day() === 0 || date.day() === 6;

                return (
                  <Box
                    key={`status-${employee.employeeId}-${day}`}
                    sx={{
                      p: 0.5,
                      textAlign: "center",
                      backgroundColor:
                        isWeekend && !statusColors[status]
                          ? themeMode === "dark"
                            ? "rgba(30,41,59,0.3)"
                            : "#fafafa"
                          : "transparent",
                      borderRadius: "4px",
                      border: `1px solid ${
                        themeMode === "dark" ? "#1e293b" : "#f1f5f9"
                      }`,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: bgColor,
                        color: textColor,
                        fontWeight: 700,
                        height: 22,
                        fontSize: "0.6rem",
                        minWidth: 28,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "4px",
                        px: 0.5,
                      }}
                    >
                      {displayText}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Confirm Attendance Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => {
          if (!confirming) {
            setConfirmDialogOpen(false);
            setRemarks("");
            setError("");
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: themeMode === "dark" ? "#0f172a" : "#ffffff",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1.2rem" }}>
          Confirm Attendance for Payroll
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Please confirm the attendance summary for{" "}
              <strong>{employee.employeeName}</strong> for the month of{" "}
              <strong>{selectedMonth.format("MMMM YYYY")} </strong>
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#d1fae5",
                  borderRadius: "8px",
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: "0.75rem", color: "#065f46" }}>
                  Present
                </Typography>
                <Typography
                  sx={{ fontWeight: 700, fontSize: "1.5rem", color: "#065f46" }}
                >
                  {summary.present}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#fef3c7",
                  borderRadius: "8px",
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: "0.75rem", color: "#92400e" }}>
                  Leave
                </Typography>
                <Typography
                  sx={{ fontWeight: 700, fontSize: "1.5rem", color: "#92400e" }}
                >
                  {summary.leave}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#fee2e2",
                  borderRadius: "8px",
                  textAlign: "center",
                }}
              >
                <Typography sx={{ fontSize: "0.75rem", color: "#991b1b" }}>
                  Absent
                </Typography>
                <Typography
                  sx={{ fontWeight: 700, fontSize: "1.5rem", color: "#991b1b" }}
                >
                  {summary.absent}
                </Typography>
              </Box>
            </Box>

            <TextField
              label="Remarks"
              multiline
              rows={3}
              fullWidth
              value={remarks}
              onChange={(e) => {
                setRemarks(e.target.value);
                if (error) setError("");
              }}
              error={!!error}
              helperText={error}
              placeholder="Add any remarks or notes for this attendance confirmation..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() => {
              setConfirmDialogOpen(false);
              setRemarks("");
              setError("");
            }}
            disabled={confirming}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmAttendance}
            disabled={confirming}
            sx={{
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              color: "#ffffff",
              borderRadius: "8px",
              px: 3,
              py: 0.75,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
              },
              "&:disabled": {
                background: "#d1d5db",
                color: "#9ca3af",
              },
            }}
          >
            {confirming ? (
              <CircularProgress size={24} sx={{ color: "#ffffff" }} />
            ) : (
              "Confirm Attendance"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Attendance Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          if (!editing) {
            setEditDialogOpen(false);
            setEditData({
              present: summary.present,
              absent: summary.absent,
              leave: summary.leave,
              remarks: "",
            });
            setError("");
          }
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor: themeMode === "dark" ? "#0f172a" : "#ffffff",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1.2rem" }}>
          Edit Attendance for Payroll
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Edit attendance summary for{" "}
              <strong>{employee.employeeName}</strong> for the month of{" "}
              <strong>{selectedMonth.format("MMMM YYYY")}</strong>
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 2,
              }}
            >
              <TextField
                label="Present Days"
                type="text"
                value={editData.present}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 0;
                  setEditData({ ...editData, present: val });
                }}
                size="small"
                InputProps={{
                  sx: { borderRadius: "8px" },
                }}
              />
              <TextField
                label="Leave Days"
                type="text"
                value={editData.leave}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 0;
                  setEditData({ ...editData, leave: val });
                }}
                size="small"
                InputProps={{
                  sx: { borderRadius: "8px" },
                }}
              />
              <TextField
                label="Absent Days"
                type="text"
                value={editData.absent}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 0;
                  setEditData({ ...editData, absent: val });
                }}
                size="small"
                InputProps={{
                  sx: { borderRadius: "8px" },
                }}
              />
            </Box>

            <TextField
              label="Remarks"
              multiline
              rows={3}
              fullWidth
              value={editData.remarks}
              onChange={(e) => {
                setEditData({ ...editData, remarks: e.target.value });
                if (error) setError("");
              }}
              error={!!error}
              helperText={error}
              placeholder="Add remarks for this attendance confirmation..."
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1 }}>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              setEditData({
                present: summary.present,
                absent: summary.absent,
                leave: summary.leave,
                remarks: "",
              });
              setError("");
            }}
            disabled={editing}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!editData.remarks.trim()) {
                setError("Please enter remarks");
                return;
              }
              setEditing(true);
              setError("");

              const data = {
                month: selectedMonth.month() + 1,
                year: selectedMonth.year(),
                employee_id: Number(employee.employeeId),
                status: "confirmed",
                total_present_days: Number(editData.present),
                total_absent_days: Number(editData.absent),
                total_leave_days: Number(editData.leave),
                remarks: editData.remarks.trim(),
              };

              const params = {
                year: year,
                month: month,
              };

              dispatch(confirmAttendanceForPayroll(data, params))
                .then((result) => {
                  setEditing(false);
                  if (result.success) {
                    setEditDialogOpen(false);
                    setEditData({
                      present: summary.present,
                      absent: summary.absent,
                      leave: summary.leave,
                      remarks: "",
                    });
                    setError("");
                  }
                })
                .catch(() => {
                  setEditing(false);
                });
            }}
            disabled={editing}
            sx={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              color: "#ffffff",
              borderRadius: "8px",
              px: 3,
              py: 0.75,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
              },
              "&:disabled": {
                background: "#d1d5db",
                color: "#9ca3af",
              },
            }}
          >
            {editing ? (
              <CircularProgress size={24} sx={{ color: "#ffffff" }} />
            ) : (
              "Confirm & Submit"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default React.memo(AttendanceMonthlyToggle);
